// Shared archetype data - pure client/server-safe constants. No AI, no fetches.
export type ArchetypeId = "explorer" | "skeptic" | "dreamer" | "tinkerer" | "storyteller";

export type Archetype = {
  id: ArchetypeId;
  name: string;
  tagline: string;
  blurb: string;
  emoji: string;
  // Category slug affinity used to personalize the feed.
  categoryAffinity: string[];
  color: string; // tailwind-ish accent class for chips/badges
};

export const ARCHETYPES: Record<ArchetypeId, Archetype> = {
  explorer: {
    id: "explorer",
    name: "The Explorer",
    tagline: "Field-trip energy. Always 'what's over there?'",
    blurb: "You want the natural world explained - animals, oceans, ancient earth. The kind of stuff that makes you reach for your phone mid-walk.",
    emoji: "🧭",
    categoryAffinity: ["weird-animals", "ocean", "ancient-earth", "dangerous-nature"],
    color: "from-emerald-500 to-teal-500",
  },
  skeptic: {
    id: "skeptic",
    name: "The Skeptic",
    tagline: "Wait, prove it.",
    blurb: "You love when reality is weirder than the myth. Physics, debunked facts, sharp evidence - you came to argue.",
    emoji: "🔍",
    categoryAffinity: ["physics-weirdness", "facts", "mysteries", "mind-illusions"],
    color: "from-sky-500 to-cyan-500",
  },
  dreamer: {
    id: "dreamer",
    name: "The Dreamer",
    tagline: "Cosmic. Vast. Pass the telescope.",
    blurb: "Space, time, scale, mystery. You want the stuff that makes you feel small in a good way.",
    emoji: "🌌",
    categoryAffinity: ["space", "mysteries", "physics-weirdness", "ancient-earth"],
    color: "from-violet-500 to-fuchsia-500",
  },
  tinkerer: {
    id: "tinkerer",
    name: "The Tinkerer",
    tagline: "Show me how it works.",
    blurb: "Mechanisms, microscopic systems, the human body. You want to crack open the machine and look inside.",
    emoji: "🔧",
    categoryAffinity: ["body", "microscopic", "physics-weirdness", "facts"],
    color: "from-amber-500 to-orange-500",
  },
  storyteller: {
    id: "storyteller",
    name: "The Storyteller",
    tagline: "Did you know about-",
    blurb: "You collect facts to share. Mysteries, illusions, mind-bending stories that make people lean in.",
    emoji: "🎙️",
    categoryAffinity: ["mysteries", "mind-illusions", "weird-animals", "facts"],
    color: "from-rose-500 to-pink-500",
  },
};

export const ARCHETYPE_LIST: Archetype[] = Object.values(ARCHETYPES);

// Quiz: 6 questions, each answer scores 0–3 archetypes. Tally → highest wins.
export type QuizQuestion = {
  q: string;
  answers: { label: string; weights: Partial<Record<ArchetypeId, number>> }[];
};

export const QUIZ: QuizQuestion[] = [
  {
    q: "It's Saturday. You disappear into a rabbit hole about…",
    answers: [
      { label: "A deep-sea creature with three hearts", weights: { explorer: 2, storyteller: 1 } },
      { label: "Why time slows down near a black hole", weights: { dreamer: 2, skeptic: 1 } },
      { label: "How your immune system actually decides", weights: { tinkerer: 2 } },
      { label: "A debunked 'fact' everyone still repeats", weights: { skeptic: 2, storyteller: 1 } },
    ],
  },
  {
    q: "A friend tells you something wild. Your first move?",
    answers: [
      { label: "'Wait, source?'", weights: { skeptic: 3 } },
      { label: "'No way - tell me MORE'", weights: { storyteller: 2, explorer: 1 } },
      { label: "Quietly google how it works", weights: { tinkerer: 2 } },
      { label: "Picture the whole thing in your head", weights: { dreamer: 2 } },
    ],
  },
  {
    q: "Pick a vibe:",
    answers: [
      { label: "Misty jungle, weird bird call", weights: { explorer: 3 } },
      { label: "Starlit desert, total silence", weights: { dreamer: 3 } },
      { label: "Lab bench, late night, soldering iron", weights: { tinkerer: 3 } },
      { label: "Cozy couch, three friends, one wild story", weights: { storyteller: 3 } },
    ],
  },
  {
    q: "Your favorite kind of fact is one that…",
    answers: [
      { label: "Breaks the rules of intuition", weights: { skeptic: 2, dreamer: 1 } },
      { label: "Makes nature feel alien", weights: { explorer: 2 } },
      { label: "Explains a hidden mechanism", weights: { tinkerer: 3 } },
      { label: "You'll definitely repeat at dinner", weights: { storyteller: 3 } },
    ],
  },
  {
    q: "Pick your weapon:",
    answers: [
      { label: "Binoculars", weights: { explorer: 3 } },
      { label: "Telescope", weights: { dreamer: 3 } },
      { label: "Microscope", weights: { tinkerer: 3 } },
      { label: "Megaphone", weights: { storyteller: 3 } },
      { label: "Magnifying glass", weights: { skeptic: 3 } },
    ],
  },
  {
    q: "Honestly, you'd rather…",
    answers: [
      { label: "Be the first to find it", weights: { explorer: 2 } },
      { label: "Be the first to disprove it", weights: { skeptic: 2 } },
      { label: "Be the first to understand it", weights: { tinkerer: 2 } },
      { label: "Be the first to imagine it", weights: { dreamer: 2 } },
      { label: "Be the first to share it", weights: { storyteller: 2 } },
    ],
  },
];

export function scoreQuiz(answers: number[]): ArchetypeId {
  const tally: Record<ArchetypeId, number> = { explorer: 0, skeptic: 0, dreamer: 0, tinkerer: 0, storyteller: 0 };
  answers.forEach((ans, i) => {
    const a = QUIZ[i]?.answers[ans];
    if (!a) return;
    for (const [k, v] of Object.entries(a.weights)) tally[k as ArchetypeId] += v as number;
  });
  let best: ArchetypeId = "explorer";
  let max = -1;
  (Object.keys(tally) as ArchetypeId[]).forEach((k) => {
    if (tally[k] > max) { max = tally[k]; best = k; }
  });
  return best;
}

// DiceBear v9 - free, open, no API key, URL-only. SVG.
export function avatarUrl(seed: string | null | undefined, style: string | null | undefined = "bottts") {
  const s = (seed && seed.length > 0 ? seed : "smartasaurus").replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 64) || "smartasaurus";
  const st = (style && ["bottts","adventurer","shapes","identicon","fun-emoji","thumbs"].includes(style)) ? style : "bottts";
  return `https://api.dicebear.com/9.x/${st}/svg?seed=${encodeURIComponent(s)}&backgroundType=gradientLinear&radius=20`;
}

export const AVATAR_STYLES = [
  { id: "bottts", label: "Bot" },
  { id: "adventurer", label: "Adventurer" },
  { id: "fun-emoji", label: "Emoji" },
  { id: "thumbs", label: "Thumbs" },
  { id: "shapes", label: "Shapes" },
  { id: "identicon", label: "Identicon" },
] as const;
